import React, { useEffect, useState, VoidFunctionComponent } from 'react';
import {
  FixedContentPortal,
  FixedContentPortalType
} from '../../fixedContent/FixedContentPortal/FixedContentPortal';
import { useEventCallback } from '../../hooks/useEventCallback';
import { useNotificationManager } from '../../hooks/useNotificationManager';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import {
  NotificationCard,
  NotificationCardProps
} from '../NotificationCard/NotificationCard';
import styles from './NotificationContainer.module.css';

export type NotificationContainerProps = PropsWithoutChildren<
  Record<never, never>
>;

export const NotificationContainer: VoidFunctionComponent<NotificationContainerProps> =
  React.memo((props) => {
    prepareProps({ props, ignoreChildren: true });

    const notificationManager = useNotificationManager();
    const [notifications, setNotifications] = useState(
      notificationManager.getNotifications()
    );
    useEffect(() => {
      const disposable =
        notificationManager.bindNotifications(setNotifications);
      return () => disposable.dispose();
    }, [notificationManager, setNotifications]);

    const handleNotificationCloseButtonClick: NotificationCardProps['onCloseButtonClick'] =
      useEventCallback((notification) => {
        notificationManager.hide(notification.id);
      });

    const handleNotificationHidden: NotificationCardProps['onHidden'] =
      useEventCallback((notification) => {
        notificationManager.remove(notification.id);
      });

    return (
      <FixedContentPortal type={FixedContentPortalType.Notification}>
        <div className={styles.container}>
          <div className={styles.contentWrapper}>
            {notifications.map((n) => (
              <NotificationCard
                key={n.id}
                notification={n}
                onHidden={handleNotificationHidden}
                onCloseButtonClick={handleNotificationCloseButtonClick}
                data-testid="NotificationContainer.Notification"
              ></NotificationCard>
            ))}
          </div>
        </div>
      </FixedContentPortal>
    );
  });
